import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Users, ThumbsUp, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function AudienceVotePage() {
  const { competitionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [votedFor, setVotedFor] = useState({});
  const [voting, setVoting] = useState(null);
  const retryRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audience/competition/${competitionId}`);
      setData(res.data);
      setConnecting(false);
      clearInterval(retryRef.current);
    } catch (error) {
      if (!error.response) {
        setConnecting(true);
        return;
      }
      if (error.response?.status === 403) {
        setData({ disabled: true });
      }
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    fetchData();
    retryRef.current = setInterval(() => {
      if (connecting) fetchData();
    }, 3000);
    return () => clearInterval(retryRef.current);
  }, [fetchData, connecting]);

  useEffect(() => {
    if (!connecting && data && !data.disabled) {
      const interval = setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [fetchData, connecting, data]);

  const handleVote = async (dancerId, dancerName) => {
    if (votedFor[dancerId]) return;
    setVoting(dancerId);
    try {
      await axios.post(`${API}/audience/vote/${competitionId}`, { dancer_id: dancerId });
      setVotedFor(prev => ({ ...prev, [dancerId]: true }));
      toast.success(`Vote for ${dancerName} recorded!`);
    } catch (error) {
      if (error.response?.status === 400) {
        setVotedFor(prev => ({ ...prev, [dancerId]: true }));
        toast.error("You already voted for this dancer");
      } else {
        toast.error("Failed to vote");
      }
    } finally {
      setVoting(null);
    }
  };

  if (loading || connecting) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#FF007F]/10 flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-[#FF007F]" />
          </div>
          <div className="w-10 h-10 border-3 border-[#FF007F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white font-bold mb-1">{connecting ? "Connecting..." : "Loading..."}</p>
          {connecting && <p className="text-[#A1A1AA] text-sm">Please wait a moment</p>}
        </div>
      </div>
    );
  }

  if (data?.disabled) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="text-center">
          <Users className="w-16 h-16 text-[#27272a] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Voting Not Available</h1>
          <p className="text-[#52525B]">Audience voting is not enabled for this competition.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="text-center">
          <Users className="w-16 h-16 text-[#27272a] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Competition Not Found</h1>
          <p className="text-[#52525B] mb-6">This link may be invalid or the competition has been removed.</p>
          <a href="/audience" className="inline-flex items-center gap-2 text-[#FF007F] hover:underline">
            <Heart className="w-4 h-4" />
            Go to Audience Voting Portal
          </a>
        </div>
      </div>
    );
  }

  const { competition, festival_name, leaders, followers } = data;

  const DancerVoteCard = ({ dancer, color, roleLabel }) => {
    const isVoted = votedFor[dancer.dancer_id];
    const isVoting = voting === dancer.dancer_id;

    return (
      <motion.div
        className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
          isVoted 
            ? 'bg-green-500/10 border-green-500/30' 
            : 'bg-white/5 border-white/10'
        }`}
        whileTap={{ scale: isVoted ? 1 : 0.98 }}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <span className="font-bold text-lg" style={{ color }}>#{dancer.bib_number}</span>
          </div>
          <div>
            <p className="text-white font-medium">{dancer.name}</p>
            <p className="text-xs" style={{ color }}>{roleLabel}</p>
          </div>
        </div>
        <Button
          size="sm"
          disabled={isVoted || isVoting}
          onClick={() => handleVote(dancer.dancer_id, dancer.name)}
          className={isVoted 
            ? "bg-green-500/20 text-green-500 cursor-default" 
            : "bg-[#FF007F]/20 text-[#FF007F] hover:bg-[#FF007F]/30"
          }
          data-testid={`vote-${dancer.dancer_id}`}
        >
          {isVoting ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : isVoted ? (
            <><Check className="w-4 h-4 mr-1" /> Voted</>
          ) : (
            <><Heart className="w-4 h-4 mr-1" /> Vote</>
          )}
        </Button>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#09090b] pb-8 relative">
      {/* J&J Watermark */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img src="/jj-logo.png" alt="" className="w-[400px] h-[400px] object-contain opacity-[0.06]" />
      </div>

      <header className="py-6 px-4 text-center border-b border-white/5 relative z-10">
        {/* Event logo */}
        {data?.festival_logo && (
          <img src={data.festival_logo} alt="Event" className="h-14 mx-auto mb-3 rounded-lg object-contain" />
        )}
        <div className="flex items-center justify-center gap-2 mb-2">
          <ThumbsUp className="w-6 h-6 text-[#FF007F]" />
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-white">Audience Vote</h1>
        </div>
        <p className="text-[#A1A1AA]">{festival_name}</p>
        <p className="text-[#FFD700] text-sm font-medium">{competition?.category}</p>
        <div className="mt-2 px-3 py-1 bg-[#FF007F]/10 rounded-full inline-block">
          <span className="text-[#FF007F] text-xs">Audience voting contributes {competition?.audience_voting_percentage}% to final score</span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {leaders?.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-[#FFD700]" />
              <h2 className="text-white font-bold text-lg">Leaders</h2>
            </div>
            <div className="space-y-3">
              {leaders.map(dancer => (
                <DancerVoteCard key={dancer.dancer_id} dancer={dancer} color="#FFD700" roleLabel="Leader" />
              ))}
            </div>
          </div>
        )}

        {followers?.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-[#FF007F]" />
              <h2 className="text-white font-bold text-lg">Followers</h2>
            </div>
            <div className="space-y-3">
              {followers.map(dancer => (
                <DancerVoteCard key={dancer.dancer_id} dancer={dancer} color="#FF007F" roleLabel="Follower" />
              ))}
            </div>
          </div>
        )}

        {(!leaders?.length && !followers?.length) && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-[#27272a] mx-auto mb-4" />
            <p className="text-[#52525B]">No dancers available for voting yet.</p>
          </div>
        )}
      </main>

      <footer className="text-center py-4">
        <a href="/terms" className="text-[#52525B] text-xs hover:text-[#A1A1AA]">Terms of Use</a>
        <p className="text-[#27272a] text-xs mt-1">JackAndJillScore</p>
      </footer>
    </div>
  );
}
